/**
 * FaturaCpfGerenteCard
 *
 * Card destacado no topo da aba "Faturas" do modal de lead, exibindo:
 *  - Botão "Pedir Fatura CPF" (abre dialog enxuto)
 *  - Lista compacta de pedidos anteriores (ENVIADA/RECEBIDA/FALHA)
 *  - Prévia sutil do que se trata
 *
 * Visível só quando a estratégia FATURA_CPF_GERENTE está ativa.
 * Isolado para evitar mexer no gigante LeadFaturaTab.tsx.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { IdCard, FileText, Clock, CheckCircle2, AlertCircle, ExternalLink, Ban } from "lucide-react";
import { FaturaCpfDialog } from "@/components/shared/FaturaCpfDialog";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

interface Props {
  leadId: string;
}

interface SolicitacaoRow {
  id: string;
  cpf: string;
  codigo_vinculo: string;
  status: "ENVIADA" | "RECEBIDA" | "FALHA" | "NAO_EXISTE";
  created_at: string;
  recebida_em: string | null;
  fatura_upload_id: string | null;
  lembrete_enviado_em: string | null;
}

interface LeadMini {
  id: string;
  cnpj: string | null;
  razao_social: string;
  lead_numero: number | null;
  doc_cpf: string | null;
  cpf: string | null;
  tipo_pessoa: string | null;
}

function formatCpfMask(cpf: string): string {
  const d = (cpf || "").replace(/\D/g, "");
  if (d.length !== 11) return cpf;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

export function FaturaCpfGerenteCard({ leadId }: Props) {
  const { profile } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Estratégia ativa?
  const { data: estrategiaAtiva } = useQuery({
    queryKey: ["estrategia-fatura-cpf-ativa-card"],
    queryFn: async () => {
      const { data } = await supabase
        .from("disparo_estrategias")
        .select("id")
        .eq("tipo", "FATURA_CPF_GERENTE")
        .eq("ativo", true)
        .limit(1)
        .maybeSingle();
      return !!data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Lead mini
  const { data: lead } = useQuery<LeadMini | null>({
    queryKey: ["lead-fatura-cpf-card", leadId],
    queryFn: async () => {
      const { data } = await supabase
        .from("leads_carteira")
        .select("id, cnpj, razao_social, lead_numero, doc_cpf, cpf, tipo_pessoa")
        .eq("id", leadId)
        .maybeSingle();
      return (data as any) || null;
    },
    enabled: !!leadId,
    staleTime: 60 * 1000,
  });

  // Solicitações anteriores deste lead
  const { data: solicitacoes = [], refetch: refetchSolicitacoes } = useQuery<SolicitacaoRow[]>({
    queryKey: ["solicitacoes-fatura-cpf-lead", leadId],
    queryFn: async () => {
      const { data } = await supabase
        .from("solicitacao_fatura_cpf")
        .select("id, cpf, codigo_vinculo, status, created_at, recebida_em, fatura_upload_id, lembrete_enviado_em")
        .eq("lead_id", leadId)
        .order("created_at", { ascending: false })
        .limit(20);
      return (data as any) || [];
    },
    enabled: !!leadId,
    staleTime: 30 * 1000,
  });

  if (!estrategiaAtiva) return null;
  if (!lead || !lead.lead_numero) return null;

  return (
    <>
      <Card className="border-border bg-[#FAF8F5] dark:bg-card">
        <CardContent className="p-3 space-y-2.5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <IdCard className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-foreground">
                  Fatura CPF (Gerente Claro)
                </h4>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                {lead.tipo_pessoa === "PF"
                  ? "Pede à Gerente Claro as contas deste cliente: a móvel em PDF e a fixa em fotos. A resposta entra aqui sozinha."
                  : "Pede à Gerente Claro as contas do CPF do sócio deste CNPJ. A resposta entra aqui sozinha."}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-7 shrink-0 gap-1.5 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
              onClick={() => setDialogOpen(true)}
            >
              <IdCard className="h-3.5 w-3.5" /> Pedir Fatura CPF
            </Button>
          </div>

          {solicitacoes.length > 0 && (
            <div className="border-t border-border pt-2 space-y-1.5">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
                Pedidos anteriores
              </div>
              {solicitacoes.map((s) => (
                <div key={s.id} className="flex items-center gap-2 text-[11px]">
                  <StatusBadge status={s.status} />
                  <span className="font-mono text-foreground/90">
                    {formatCpfMask(s.cpf)}
                  </span>
                  <span className="text-muted-foreground">
                    · {formatDistanceToNow(new Date(s.created_at), { addSuffix: true, locale: ptBR })}
                  </span>
                  {s.lembrete_enviado_em && (
                    <span className="text-muted-foreground" title="Lembrete já enviado">
                      · lembrete enviado
                    </span>
                  )}
                  {s.fatura_upload_id && (
                    <a
                      href={`/faturas?id=${s.fatura_upload_id}`}
                      className="ml-auto inline-flex items-center gap-0.5 text-primary hover:underline"
                    >
                      Ver PDF <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {dialogOpen && (
        <FaturaCpfDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          lead={lead}
          consultorId={profile?.id || ""}
          onSuccess={() => refetchSolicitacoes()}
        />
      )}
    </>
  );
}

function StatusBadge({ status }: { status: SolicitacaoRow["status"] }) {
  if (status === "RECEBIDA") {
    return (
      <Badge variant="outline" className="text-[10px] gap-1 bg-success/5 border-success/40 text-success dark:bg-success/40 dark:border-success dark:text-success">
        <CheckCircle2 className="h-2.5 w-2.5" /> Recebida
      </Badge>
    );
  }
  if (status === "FALHA") {
    return (
      <Badge variant="outline" className="text-[10px] gap-1 bg-muted border-border text-brand-rose dark:bg-muted/40 dark:border-brand-rose dark:text-brand-rose">
        <AlertCircle className="h-2.5 w-2.5" /> Falha
      </Badge>
    );
  }
  if (status === "NAO_EXISTE") {
    return (
      <Badge variant="outline" className="text-[10px] gap-1 bg-muted/40 border-muted-foreground/30 text-muted-foreground">
        <Ban className="h-2.5 w-2.5" /> Não existe
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px] gap-1 bg-primary/5 border-primary/40 text-primary">
      <Clock className="h-2.5 w-2.5" /> Aguardando
    </Badge>
  );
}
